// src/services/employeesService.ts
// Fetches live employees from the Cloudflare Worker API (Neon database).
// Maps the API response to the Employee shape Employees.tsx expects.
//
// Field notes:
//   employees.name          → firstName + lastName + fullName (split on first space)
//   employees.role          → trade (role-to-trade heuristic; refined when a trade
//                             column is added to the schema in a future migration)
//   employees.classification→ payClassification
//   employees.defaultShift  → kept as-is for roster use
//
// Fields not yet in the schema (credentials, emergencyContact, apprentice data,
// workerComp, etc.) default to safe empty values. They will be populated when
// those sub-tables (employee_credentials, leave_balances) are joined in a
// future API step.

import { api } from '../utils/api'

// What the API returns from GET /api/employees (slim list projection)
interface ApiEmployee {
  id:             string
  employeeNumber?: string
  employee_number?: string
  name?:          string
  role?:          string
  classification?: string | null
  defaultShift?:  string
  default_shift?: string
  phone?:         string | null
  email?:         string | null
  isActive?:      boolean
  is_active?:     boolean
  startDate?:     string | null
  start_date?:    string | null
}

// What Employees.tsx expects — matches typeof MOCK_EMPLOYEES[0]
export interface MappedEmployee {
  id:                  string
  employeeNumber:      string
  firstName:           string
  lastName:            string
  fullName:            string
  email:               string
  phone:               string
  dateOfBirth:         string | null
  address:             string
  emergencyContact:    string
  emergencyPhone:      string
  trade:               string
  employmentType:      string
  payClassification:   string
  systemTier:          string
  currentJobId:        string | null
  currentJobNumber:    string | null
  startDate:           string
  defaultShift:        string
  credentials: {
    electricalLicenceNumber: string | null
    electricalLicenceExpiry: string | null
    firstAidExpiry:          string | null
    whsInductionExpiry:      string | null
    tcaExpiry:               string | null
    workingAtHeights:        boolean
    confinedSpaceEntry:      boolean
    hvAuthority:             string | null
  }
}

// Heuristic: map DB role string to the trade keys used in TRADE_LABELS
function roleToTrade(role: string | undefined): string {
  const r = (role ?? '').toUpperCase()
  if (r.includes('CIVIL'))                          return 'CIVIL'
  if (r.includes('TRAFFIC') || r.includes('TC'))   return 'TRAFFIC_CONTROL'
  if (r.includes('ITS')    || r.includes('COMMS')) return 'ITS'
  if (r.includes('ADMIN'))                          return 'ADMINISTRATION'
  return 'ELECTRICAL'   // default for electrical contractor staff
}

function mapApiEmployeeToAppEmployee(raw: ApiEmployee): MappedEmployee {
  const fullName   = raw.name ?? ''
  const nameParts  = fullName.trim().split(/\s+/)
  const firstName  = nameParts[0] ?? fullName
  const lastName   = nameParts.slice(1).join(' ') || '—'

  return {
    id:               raw.id,
    employeeNumber:   raw.employeeNumber ?? raw.employee_number ?? '',
    firstName,
    lastName,
    fullName,
    email:            raw.email    ?? '',
    phone:            raw.phone    ?? '',
    dateOfBirth:      null,
    address:          '',
    emergencyContact: '',
    emergencyPhone:   '',
    trade:            roleToTrade(raw.role),
    employmentType:   'FULL_TIME',                   // default; employment_type column planned
    payClassification: raw.classification ?? 'ELECTRICAL_A_GRADE',
    systemTier:       'D_FIELD',                     // default; tier column planned
    currentJobId:     null,                          // enriched from roster in a future step
    currentJobNumber: null,
    startDate:        raw.startDate ?? raw.start_date ?? new Date().toISOString().slice(0, 10),
    defaultShift:     raw.defaultShift ?? raw.default_shift ?? 'DAY',
    // Credentials come from employee_credentials table — not yet joined in list endpoint.
    // Will be populated from GET /api/employees/:id in the detail view (Phase 2 step).
    credentials: {
      electricalLicenceNumber: null,
      electricalLicenceExpiry: null,
      firstAidExpiry:          null,
      whsInductionExpiry:      null,
      tcaExpiry:               null,
      workingAtHeights:        false,
      confinedSpaceEntry:      false,
      hvAuthority:             null,
    },
  }
}

export async function fetchEmployees(): Promise<MappedEmployee[]> {
  const { data } = await api.get<{ data: ApiEmployee[] } | ApiEmployee[]>('/api/employees')
  const raw: ApiEmployee[] = Array.isArray(data) ? data : ((data as any).data ?? [])
  return raw.map(mapApiEmployeeToAppEmployee)
}

// UUID pattern — Neon employees have UUID IDs; mock employees have 'e1', 'e2' etc.
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

export function isNeonEmployee(id: string): boolean {
  return UUID_RE.test(id)
}

export async function updateEmployee(
  id: string,
  updates: Partial<MappedEmployee> & { position?: string },
): Promise<void> {
  if (!isNeonEmployee(id)) return   // mock employees — no-op, local edit is sufficient
  await api.put(`/api/employees/${id}`, updates)
}
